import { useState, useRef, useCallback, useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { toast } from "@/lib/toast";
import { trpc } from "@/lib/trpc";
import {
  parseAppointmentsPdf,
  type ParsedAppointmentsPdf,
} from "@/lib/appointmentsPdf";
import { useApplyDefaultLocation } from "@/hooks/useDefaultLocation";
import {
  Upload,
  FileText,
  MapPin,
  Loader2,
  CheckCircle2,
  RotateCcw,
  X,
  AlertTriangle,
} from "lucide-react";

const LOCATIONS = ["Iowa", "Minnesota", "Colorado"] as const;
type Location = (typeof LOCATIONS)[number];

const LOCATION_DRIVE_URLS: Record<Location, string> = {
  Iowa: "https://drive.google.com/drive/folders/17OhaVA7QqOD5qRA8ZJxFR_pk9lwNWBF0",
  Minnesota: "https://drive.google.com/drive/folders/1mxVtgreKjMMoD_QmvJ_EzE51Ff1Yta2r",
  Colorado: "https://drive.google.com/drive/folders/1u7hzmzdjWjh4botrKCV50mgeanrgKIie",
};

// Active-state classes for each location chip. Iowa Blue + MN Purple match
// the codebase's existing location-accent colors (see Footage Search A-Roll
// chip, Topic Tag library page). Colorado keeps the default primary green.
const LOCATION_ACTIVE_CLASSES: Record<Location, string> = {
  Iowa: "border-[#5B7C99] bg-[#5B7C99] text-white",
  Minnesota: "border-[#6A5ACD] bg-[#6A5ACD] text-white",
  Colorado: "border-primary bg-primary text-primary-foreground",
};

const WEEKDAY_ORDER = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

interface ParsedFile {
  fileName: string;
  /** "monday.csv" / "tuesday.csv" — derived from sourceDateLabel's weekday */
  csvName: string;
  parsed: ParsedAppointmentsPdf;
}

function weekdayCsvNameFromLabel(label: string): string {
  // sourceDateLabel is "Tuesday, June 23, 2026" — take the lowercase day name.
  const m = /^([A-Za-z]+),/.exec(label);
  return `${(m?.[1] ?? "appointments").toLowerCase()}.csv`;
}

function weekdayRank(csvName: string): number {
  const i = WEEKDAY_ORDER.indexOf(csvName.replace(/\.csv$/, ""));
  return i === -1 ? WEEKDAY_ORDER.length : i;
}

/**
 * Single Boards upload. Drop any mix of one week's daily "Appointments For:"
 * PDFs; the week's dated subfolder is found-or-created from the PDFs' header
 * date (not the filename), and each day lands as <weekday>.csv. No Monday PDF
 * is needed — on Monday-holiday weeks Tuesday (or whichever day comes first)
 * creates the folder. The weekly-table Apps Scripts key off the folder name's
 * leading Monday date, which weekFolderNameFromDate always produces.
 */
export default function BoardsUploadWorkflow() {
  const [parsing, setParsing] = useState(false);
  const [parsedFiles, setParsedFiles] = useState<ParsedFile[]>([]);
  const [activeTab, setActiveTab] = useState(0);
  const [location, setLocation] = useState<Location | null>(null);
  useApplyDefaultLocation(location, setLocation);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [success, setSuccess] = useState<{
    location: Location;
    subfolderName: string;
    files: ParsedFile[];
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadMutation = trpc.boards.uploadWeek.useMutation();


  const weekNames = useMemo(
    () => Array.from(new Set(parsedFiles.map((p) => p.parsed.weekFolderName))),
    [parsedFiles],
  );
  const duplicateCsvNames = useMemo(() => {
    const seen = new Set<string>();
    const dupes = new Set<string>();
    for (const p of parsedFiles) {
      if (seen.has(p.csvName)) dupes.add(p.csvName);
      seen.add(p.csvName);
    }
    return Array.from(dupes);
  }, [parsedFiles]);
  const mixedWeeks = weekNames.length > 1;
  const blockingIssue = duplicateCsvNames.length > 0 || mixedWeeks;
  const weekFolderName = weekNames[0];

  const handleFiles = useCallback(async (files: FileList | File[]) => {
    const arr = Array.from(files).filter((f) => /\.pdf$/i.test(f.name));
    if (arr.length === 0) {
      toast.error("Only .pdf files are accepted.");
      return;
    }
    setParsing(true);
    const next: ParsedFile[] = [];
    for (const f of arr) {
      try {
        const parsed = await parseAppointmentsPdf(f);
        if (parsed.rows.length === 0) {
          toast.warning(`${f.name}: parsed, but no appointment rows were detected.`);
        }
        next.push({
          fileName: f.name,
          csvName: weekdayCsvNameFromLabel(parsed.sourceDateLabel),
          parsed,
        });
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        toast.error(`${f.name}: ${msg}`);
      }
    }
    setParsedFiles((prev) =>
      [...prev, ...next].sort((a, b) => weekdayRank(a.csvName) - weekdayRank(b.csvName)),
    );
    setActiveTab(0);
    setParsing(false);
    if (next.length > 0) {
      toast.success(`Parsed ${next.length} PDF${next.length === 1 ? "" : "s"}`);
    }
  }, []);

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files?.length) void handleFiles(e.dataTransfer.files);
  };

  const removeFile = (idx: number) => {
    setParsedFiles((prev) => prev.filter((_, i) => i !== idx));
    setActiveTab((t) => Math.max(0, Math.min(t, parsedFiles.length - 2)));
  };

  const resetAll = () => {
    setParsedFiles([]);
    setActiveTab(0);
    setLocation(null);
    setSuccess(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const onConfirmUpload = async () => {
    if (!location || !weekFolderName || parsedFiles.length === 0 || blockingIssue) return;
    setUploading(true);
    try {
      await uploadMutation.mutateAsync({
        location,
        weekFolderName,
        files: parsedFiles.map((p) => ({ fileName: p.csvName, csvText: p.parsed.csvText })),
      });
      setSuccess({ location, subfolderName: weekFolderName, files: parsedFiles });
      toast.success(
        `Uploaded ${parsedFiles.length} CSV${parsedFiles.length === 1 ? "" : "s"} to ${location} / ${weekFolderName}`,
      );
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      toast.error(`Drive upload failed: ${msg}`);
    } finally {
      setUploading(false);
    }
  };

  const activeParsed = parsedFiles[activeTab];
  const canUpload = !!location && !!weekFolderName && !blockingIssue && !uploading;

  // ── Success screen ───────────────────────────────────────────────────────
  if (success) {
    return (
      <Card>
        <CardContent className="pt-8 pb-6 flex flex-col items-center text-center gap-4">
          <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/40 flex items-center justify-center">
            <CheckCircle2 className="w-9 h-9 text-green-600 dark:text-green-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold">Upload complete</h3>
            <p className="text-sm text-muted-foreground mt-1">
              {success.files.length} CSV{success.files.length === 1 ? "" : "s"} saved to{" "}
              <span className="font-medium">{success.location}</span> /{" "}
              <span className="font-medium">{success.subfolderName}</span>
            </p>
          </div>

          <SuccessBatchPreview files={success.files} />

          <div className="flex gap-3 mt-2">
            <Button asChild variant="outline">
              <a
                href={LOCATION_DRIVE_URLS[success.location]}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open in Google Drive
              </a>
            </Button>
            <Button onClick={resetAll}>
              <RotateCcw className="w-4 h-4 mr-2" />
              Upload more
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Step 1 — Drop one or more PDFs */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="w-4 h-4" /> Appointment PDFs
          </CardTitle>
          <CardDescription>
            Drop any of the week's "Appointments For:" PDFs, one day or all five. The week's
            folder is created automatically if it doesn't exist yet.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div
            onDrop={onDrop}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onClick={() => fileInputRef.current?.click()}
            className={cn(
              "flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed p-8 cursor-pointer transition-colors",
              dragOver
                ? "border-primary bg-primary/5"
                : "border-border hover:border-primary/40 hover:bg-muted/30",
              parsing && "opacity-60 pointer-events-none",
            )}
          >
            {parsing ? (
              <>
                <Loader2 className="w-7 h-7 text-muted-foreground animate-spin" />
                <p className="text-sm text-muted-foreground">Parsing…</p>
              </>
            ) : (
              <>
                <Upload className="w-7 h-7 text-muted-foreground" />
                <p className="text-sm font-medium">Drop PDFs here, or click to browse</p>
                <p className="text-xs text-muted-foreground">
                  One or many · .pdf only
                </p>
              </>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf,.pdf"
            multiple
            className="hidden"
            onChange={(e) => {
              if (e.target.files?.length) void handleFiles(e.target.files);
              e.target.value = "";
            }}
          />

          {/* Parsed-file chips */}
          {parsedFiles.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-4">
              {parsedFiles.map((p, i) => (
                <span
                  key={`${p.fileName}-${i}`}
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium",
                    i === activeTab ? "bg-primary/10 border-primary" : "bg-muted/30",
                  )}
                >
                  <button
                    type="button"
                    className="cursor-pointer"
                    onClick={() => setActiveTab(i)}
                  >
                    {p.csvName} · {p.parsed.rows.length} row
                    {p.parsed.rows.length === 1 ? "" : "s"}
                  </button>
                  <button
                    type="button"
                    aria-label={`Remove ${p.fileName}`}
                    onClick={() => removeFile(i)}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}

          {blockingIssue && (
            <div className="mt-4 rounded-lg border p-3 text-xs flex gap-2 border-red-300 bg-red-50 text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              <div className="space-y-1">
                {duplicateCsvNames.length > 0 && (
                  <p>
                    More than one PDF is for the same day ({duplicateCsvNames.join(", ")}). Remove
                    the extra one before uploading.
                  </p>
                )}
                {mixedWeeks && (
                  <p>
                    These PDFs span more than one week ({weekNames.join(", ")}). Upload one week
                    at a time.
                  </p>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Step 2 — Per-file preview (tabbed) */}
      {activeParsed && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Preview</CardTitle>
            <CardDescription>
              <span className="font-mono">{activeParsed.csvName}</span> from{" "}
              <span className="font-mono">{activeParsed.fileName}</span> · target week{" "}
              <span className="font-mono">{activeParsed.parsed.weekFolderName}</span>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <CsvPreviewBlock parsed={activeParsed.parsed} />
          </CardContent>
        </Card>
      )}

      {/* Step 3 — Location + confirm */}
      {parsedFiles.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <MapPin className="w-4 h-4" /> Destination
            </CardTitle>
            <CardDescription>Which location's Google Drive should this week land in?</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <div className="flex flex-wrap gap-2">
              {LOCATIONS.map((loc) => (
                <button
                  key={loc}
                  type="button"
                  onClick={() => setLocation(loc)}
                  className={cn(
                    "px-4 py-2 rounded-full border-2 text-sm font-medium transition-colors",
                    location === loc
                      ? LOCATION_ACTIVE_CLASSES[loc]
                      : "border-border hover:border-primary/40 hover:bg-muted/40",
                  )}
                >
                  {loc}
                </button>
              ))}
            </div>

            {location && weekFolderName && !mixedWeeks && (
              <div className="rounded-lg border bg-muted/30 p-3 text-sm">
                <p className="text-muted-foreground">Will save to:</p>
                <p className="font-mono mt-0.5">
                  {location} / {weekFolderName}
                </p>
                <ul className="font-mono text-xs text-muted-foreground mt-2 space-y-0.5">
                  {parsedFiles.map((p, i) => (
                    <li key={i}>· {p.csvName}</li>
                  ))}
                </ul>
                <p className="text-xs text-muted-foreground mt-2">
                  The subfolder is created if it doesn't already exist. Any existing CSVs with
                  these names in it will be overwritten.
                </p>
              </div>
            )}

            <div>
              <Button onClick={onConfirmUpload} disabled={!canUpload} size="lg">
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading…
                  </>
                ) : (
                  `Confirm & upload ${parsedFiles.length} CSV${parsedFiles.length === 1 ? "" : "s"} to Drive`
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function SuccessBatchPreview({ files }: { files: ParsedFile[] }) {
  const [activeIdx, setActiveIdx] = useState(0);
  const active = files[activeIdx];
  if (!active) return null;
  return (
    <div className="w-full">
      <div className="flex flex-wrap gap-2 mb-2 justify-center">
        {files.map((f, i) => (
          <button
            key={i}
            type="button"
            onClick={() => setActiveIdx(i)}
            className={cn(
              "px-3 py-1 rounded-full border text-xs font-medium font-mono",
              i === activeIdx
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-muted/30 hover:bg-muted/60",
            )}
          >
            {f.csvName}
          </button>
        ))}
      </div>
      <CsvPreviewBlock parsed={active.parsed} />
    </div>
  );
}

function CsvPreviewBlock({ parsed }: { parsed: ParsedAppointmentsPdf }) {
  return (
    <div className="w-full">
      <div className="rounded-lg border overflow-hidden">
        <div className="max-h-[360px] overflow-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-muted/80 backdrop-blur z-10">
              <tr>
                {(["Time", "Patient", "Provider", "Appointment Type"] as const).map((h) => (
                  <th key={h} className="text-left font-semibold px-3 py-2 border-b">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {parsed.rows.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-3 py-6 text-center text-muted-foreground">
                    No appointment rows detected in this PDF.
                  </td>
                </tr>
              ) : (
                parsed.rows.map((r, i) => (
                  <tr key={i} className={i % 2 === 1 ? "bg-muted/20" : undefined}>
                    <td className="px-3 py-1.5 whitespace-nowrap font-mono">{r.time}</td>
                    <td className="px-3 py-1.5">{r.patient}</td>
                    <td className="px-3 py-1.5">{r.provider}</td>
                    <td className="px-3 py-1.5">{r.appointmentType}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      <p className="text-xs text-muted-foreground mt-1.5">
        {parsed.rows.length} row{parsed.rows.length === 1 ? "" : "s"} · {parsed.sourceDateLabel}
      </p>
    </div>
  );
}
